<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import DenseTable from '@/components/ui/DenseTable.vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import Paginator from 'primevue/paginator'
import Tag from 'primevue/tag'
import Message from 'primevue/message'
import { useAuthStore } from '@/stores/auth'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { productApi, setApi, type CatalogEntry, type CatalogSet, type SetType } from '@/services/catalog'
import { formatCurrency } from '@/utils/format'

/**
 * El listado de un tipo de catálogo: productos o sets.
 *
 * Hay dos pantallas y este es el cuerpo de las dos. Lo que cambia entre ellas es
 * qué se pide, qué columnas se pintan y si hay filtro de tipo, y eso entra por
 * `kind`. Todo lo demás —el debounce de la búsqueda, la paginación, los estados de
 * carga, error y vacío— es idéntico, y duplicarlo en dos archivos lo haría divergir
 * en el primer cambio.
 *
 * Antes era un listado único con un filtro de «Tipo» que se aplicaba **en el
 * cliente**, sobre la página ya paginada. Dos consecuencias: una página podía
 * traer menos elementos de los que decía la paginación, y el total que se veía era
 * el del catálogo entero. Con dos pantallas y el filtro en el servidor, el total
 * siempre es el de lo que se está viendo.
 *
 * En escritorio se usa `DenseTable` y en móvil una lista de cards: una tabla de 6
 * columnas en 375px obliga a scroll horizontal y esconde la referencia, que es la
 * clave natural que el usuario lee.
 *
 * `loading`, `error` e `items` se guardan por separado a propósito: con un solo
 * booleano de «cargando» un fallo de red se pintaría como «no hay nada», que es un
 * diagnóstico falso y hace que el usuario repita acciones que ya funcionaban.
 */

const props = defineProps<{
  kind: 'products' | 'sets'
}>()

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const layout = useBreakpoint()

const isDesktop = computed(() => layout.value === 'desktop')
const canManage = computed(() => authStore.hasPermission('catalog.manage'))

/** Los sets tienen tipo; los productos no, así que no hay nada que filtrar. */
const tieneTipo = computed(() => props.kind === 'sets')

const esSet = computed(() => props.kind === 'sets')

const titulo = computed(() => (esSet.value ? 'Sets' : 'Productos'))
const otroKind = computed<'products' | 'sets'>(() => (esSet.value ? 'products' : 'sets'))
const otroTitulo = computed(() => (esSet.value ? 'Productos' : 'Sets'))

const items = ref<Array<CatalogEntry | CatalogSet>>([])
const loading = ref(false)
const errorMessage = ref<string | null>(null)
const search = ref('')
const typeFilter = ref<SetType | ''>('')
const page = ref(1)
const total = ref(0)
const perPage = 20

const typeOptions: Array<{ label: string; value: SetType | '' }> = [
  { label: 'Nacional y premium', value: '' },
  { label: 'Solo nacional', value: 'nacional' },
  { label: 'Solo premium', value: 'premium' },
]

/**
 * El buscador se escribe con el término de la URL, no con un estado aparte.
 *
 * El buscador es la forma más rápida de llegar a una fila concreta, así que su
 * resultado tiene que poder pegarse en un chat y volver a abrir lo mismo. Con el
 * término en un `ref` y un botón de «buscar», recargar la página lo perdía.
 */
function readSearchFromUrl(): void {
  const valor = route.query.search
  search.value = (Array.isArray(valor) ? valor[0] : valor) ?? ''
  page.value = 1
}

/** El tipo también viaja en la URL, porque es un filtro y los filtros se comparten. */
function readTypeFromUrl(): void {
  const valor = route.query.type
  const tipo = (Array.isArray(valor) ? valor[0] : valor) ?? ''
  typeFilter.value = (tipo === 'nacional' || tipo === 'premium' ? tipo : '') as SetType | ''
}

/**
 * Columnas de la tabla.
 *
 * Se declara el tipo explícitamente porque las de precio llevan `bodyClass` y sin
 * él TypeScript inferiría un tipo más estrecho que el que `DenseTable` acepta.
 */
interface ColumnaListado {
  field: string
  header: string
  bodyClass?: string
}

const columns = computed<ColumnaListado[]>(() => {
  const base: ColumnaListado[] = [
    { field: 'reference', header: 'Referencia' },
    { field: 'name', header: 'Nombre' },
  ]

  // La columna de categoría solo existe en sets. En productos, una columna que
  // dijera siempre «—» ocuparía sitio sin decir nada.
  if (tieneTipo.value) {
    base.push({ field: 'type_label', header: 'Categoría' })
  }

  base.push(
    { field: 'retail_price_label', header: 'Minorista', bodyClass: 'text-right' },
    { field: 'wholesale_price_label', header: 'Mayorista', bodyClass: 'text-right' },
  )

  return base
})

/**
 * Filas de la tabla. `DenseTable` imprime `row[field]`, así que el formateo de
 * moneda se resuelve una vez por fila y no con un `template` por columna.
 *
 * Las acciones ya no llevan el tipo: cada fila va a una ruta que lo dice, porque la
 * pantalla sabe de qué está hecha.
 */
const rows = computed(() =>
  items.value.map((item) => ({
    ...item,
    type_label: esSet.value ? (item as CatalogSet).type : '—',
    retail_price_label: formatCurrency(item.retail_price),
    wholesale_price_label: formatCurrency(item.wholesale_price),
    actions: canManage.value
      ? [
          { label: 'Editar', value: 'edit', icon: 'pi pi-pencil' },
          { label: 'Borrar', value: 'delete', icon: 'pi pi-trash', severity: 'danger' },
        ]
      : [{ label: 'Ver detalle', value: 'view', icon: 'pi pi-eye' }],
  })),
)

const hasFilters = computed(() => search.value.trim() !== '' || typeFilter.value !== '')

const isEmpty = computed(() => !loading.value && errorMessage.value === null && items.value.length === 0)

const rangeLabel = computed(() => {
  if (total.value === 0) return ''
  const first = (page.value - 1) * perPage + 1
  const last = Math.min(page.value * perPage, total.value)
  return `${first}–${last} de ${total.value}`
})

function extractErrorMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return (
    maybeAxios.response?.data?.error?.message ??
    `No se pudieron cargar los ${titulo.value.toLowerCase()}. Revisa tu conexión e inténtalo de nuevo.`
  )
}

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = null

  try {
    const params = {
      search: search.value.trim() || undefined,
      type: typeFilter.value || undefined,
      page: page.value,
      per_page: perPage,
    }

    const { data } = esSet.value
      ? await setApi.list(params)
      // El tipo no se manda en productos: el backend lo rechaza con un 422, y el
      // control ni siquiera está en pantalla.
      : await productApi.list({ search: params.search, page: params.page, per_page: params.per_page })

    items.value = data.data
    total.value = data.meta.total
  } catch (cause: unknown) {
    items.value = []
    total.value = 0
    errorMessage.value = extractErrorMessage(cause)
  } finally {
    loading.value = false
  }
}

let debounceHandle: ReturnType<typeof setTimeout> | null = null

watch(search, () => {
  page.value = 1
  if (debounceHandle !== null) clearTimeout(debounceHandle)
  // Sin debounce, cada tecla dispara una petición y las respuestas pueden llegar
  // desordenadas, pintando resultados de un término anterior.
  debounceHandle = setTimeout(() => {
    void load()
  }, 300)
})

watch(typeFilter, () => {
  page.value = 1
  void load()
})

watch(page, () => {
  void load()
})

onMounted(() => {
  readSearchFromUrl()
  readTypeFromUrl()
  void load()
})

/** Escribe los filtros en la URL para que la vista se pueda compartir y recargar. */
async function syncUrl(): Promise<void> {
  const query: Record<string, string> = {}

  if (search.value.trim() !== '') query.search = search.value.trim()
  if (typeFilter.value !== '') query.type = typeFilter.value

  await router.replace({ name: `catalog.${props.kind}.index`, query })
}

watch(search, () => {
  void syncUrl()
})

watch(typeFilter, () => {
  void syncUrl()
})

/**
 * `DenseTable` entrega las filas como `unknown` porque su contrato es genérico. Se
 * estrechan aquí: la fila siempre viene de `rows`, y si no lo estuviera el listado
 * estaría roto.
 */
function handleAction(row: unknown, action: string): void {
  const item = row as CatalogEntry

  if (action === 'view' || action === 'edit') {
    void router.push({ name: `catalog.${props.kind}.show`, params: { id: String(item.id) } })
    return
  }

  // El borrado vive en ShowView: desde la lista se llega al detalle y se borra con
  // confirmación, para no abrir un diálogo que solo recibe el id.
  if (action === 'delete') {
    void router.push({
      name: `catalog.${props.kind}.show`,
      params: { id: String(item.id) },
      query: { action: 'delete' },
    })
  }
}

function onPageChange(event: { page: number }): void {
  page.value = event.page + 1
}

function clearFilters(): void {
  search.value = ''
  typeFilter.value = ''
  page.value = 1
  void load()
}

/**
 * La categoría de un set, en palabras.
 *
 * Se escribe en pantalla en vez de dejar la clave de la base: «premium» es un dato
 * de almacenamiento y «Premium» es lo que el usuario entiende.
 */
function tipoEtiqueta(tipo: SetType | null): string {
  return tipo === 'premium' ? 'Premium' : 'Nacional'
}

/** El color distingue el set premium del nacional de un vistazo. */
function tipoSeverity(tipo: SetType | null): string {
  return tipo === 'premium' ? 'warning' : 'secondary'
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          {{ titulo }}
        </h1>
        <p class="mt-1 text-surface-500 dark:text-surface-400">
          {{ esSet ? 'Conjuntos nacionales y premium con sus componentes' : 'Piezas sueltas con sus precios minorista y mayorista' }}
        </p>
      </div>
      <Button
        v-if="canManage"
        :label="`Nuevo ${esSet ? 'set' : 'producto'}`"
        icon="pi pi-plus"
        @click="router.push({ name: `catalog.${kind}.create` })"
      />
    </header>

    <!--
      El enlace a la otra pantalla va siempre visible, no solo cuando hay algo en
      lista. Es la forma de pasar de productos a sets sin volver a la navegación, y
      por debajo del listado vacío no se vería nunca.
    -->
    <nav aria-label="Otras pantallas del catálogo">
      <RouterLink
        :to="{ name: `catalog.${otroKind}.index` }"
        class="inline-flex items-center gap-1.5 text-sm text-primary-700 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:text-primary-300"
      >
        <i
          class="pi pi-arrow-left text-xs"
          aria-hidden="true"
        />
        Ver {{ otroTitulo.toLowerCase() }}
      </RouterLink>
    </nav>

    <Message
      v-if="!canManage"
      severity="info"
      :closable="false"
      variant="simple"
    >
      Tienes acceso de lectura al catálogo. Crear y modificar requieren permiso de gestión.
    </Message>

    <AppCard>
      <template #header>
        <form
          class="grid grid-cols-1 gap-3 sm:grid-cols-2"
          role="search"
          @submit.prevent="syncUrl"
        >
          <InputText
            id="catalog-search"
            v-model="search"
            :placeholder="`Buscar ${esSet ? 'sets' : 'productos'} por referencia o nombre`"
            :aria-label="`Buscar en ${titulo.toLowerCase()}`"
            class="w-full"
          />

          <!--
            El filtro de tipo solo existe para los sets. Un producto no tiene esa
            columna, así que el control no podría hacer nada: ofrecerlo y verlo
            cambiar la lista sería un fallo, y no ofrecerlo evita la pregunta.
          -->
          <Select
            v-if="tieneTipo"
            id="catalog-type"
            v-model="typeFilter"
            placeholder="Categoría"
            input-id="catalog-type"
            :options="typeOptions"
            option-label="label"
            option-value="value"
            aria-label="Filtrar por categoría de set"
            class="w-full"
          />
        </form>
      </template>

      <!-- Estado de error: separado del vacío para no mentir sobre la causa. -->
      <div
        v-if="errorMessage"
        class="flex flex-col items-center gap-3 py-12 text-center"
        role="alert"
      >
        <i class="pi pi-exclamation-triangle text-4xl text-amber-500" />
        <p class="font-medium text-surface-900 dark:text-surface-100">
          No se pudo cargar el catálogo
        </p>
        <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
          {{ errorMessage }}
        </p>
        <Button
          label="Reintentar"
          icon="pi pi-refresh"
          severity="secondary"
          @click="load"
        />
      </div>

      <!-- Estado vacío -->
      <div
        v-else-if="isEmpty"
        class="flex flex-col items-center gap-3 py-12 text-center"
      >
        <i class="pi pi-inbox text-4xl text-surface-300 dark:text-surface-600" />
        <p class="font-medium text-surface-900 dark:text-surface-100">
          {{ hasFilters ? 'Sin resultados' : `Todavía no hay ${titulo.toLowerCase()}` }}
        </p>
        <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
          {{
            hasFilters
              ? 'Prueba con otra referencia o quita los filtros aplicados.'
              : esSet
                ? 'Registra el primer conjunto para poder añadirlo a una cotización.'
                : 'Registra la primera pieza para poder añadirla a una cotización.'
          }}
        </p>
        <Button
          v-if="hasFilters"
          label="Quitar filtros"
          severity="secondary"
          outlined
          @click="clearFilters"
        />
        <Button
          v-else-if="canManage"
          :label="`Crear el primer ${esSet ? 'set' : 'producto'}`"
          icon="pi pi-plus"
          @click="router.push({ name: `catalog.${kind}.create` })"
        />
      </div>

      <!-- Estado con datos -->
      <template v-else>
        <DenseTable
          v-if="isDesktop"
          :columns="columns"
          :rows="rows"
          :loading="loading"
          @row-click="handleAction($event, 'view')"
          @action="handleAction"
        />

        <ul
          v-else
          class="divide-y divide-surface-200 dark:divide-surface-700"
          data-testid="catalog-mobile-list"
        >
          <li
            v-for="row in items"
            :key="row.id"
            class="py-3"
          >
            <div class="flex items-start justify-between gap-3">
              <!--
                El nombre se parte en varias lineas y no se recorta.

                `truncate` deja el nombre a lo que quepa en el ancho de la
                pantalla y el resto se pierde detras de unos puntos suspensivos.
                En una lista de productos es justo el dato que se busca, asi que
                se deja que baje de linea: una referencia como
                `ESTAMPADO, MODIFICACIONES LOGOS, LOGO, DOMICILIO` no cabe en un
                movil ni partida en dos.

                `break-words` parte tambien por la mitad de una palabra suelta
                demasiado larga, que sin eso desborda la linea en vez de cortarse.
              -->
              <div class="min-w-0">
                <p class="break-words font-medium text-surface-900 dark:text-surface-100">
                  {{ row.name }}
                </p>
                <p class="break-words font-mono text-sm text-surface-500 dark:text-surface-400">
                  {{ row.reference }}
                </p>
              </div>
              <Tag
                v-if="tieneTipo"
                :value="tipoEtiqueta((row as CatalogSet).type)"
                :severity="tipoSeverity((row as CatalogSet).type)"
              />
            </div>

            <dl class="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt class="text-surface-500 dark:text-surface-400">
                Minorista
              </dt>
              <dd class="text-right font-medium text-surface-900 dark:text-surface-100">
                {{ formatCurrency(row.retail_price) }}
              </dd>
              <dt class="text-surface-500 dark:text-surface-400">
                Mayorista
              </dt>
              <dd class="text-right font-medium text-surface-900 dark:text-surface-100">
                {{ formatCurrency(row.wholesale_price) }}
              </dd>
            </dl>

            <div class="mt-3 flex flex-wrap gap-2">
              <Button
                size="small"
                severity="secondary"
                outlined
                label="Ver"
                icon="pi pi-eye"
                @click="router.push({ name: `catalog.${kind}.show`, params: { id: String(row.id) } })"
              />
              <Button
                v-if="canManage"
                size="small"
                severity="secondary"
                outlined
                label="Editar"
                icon="pi pi-pencil"
                @click="router.push({ name: `catalog.${kind}.show`, params: { id: String(row.id) }, query: { action: 'edit' } })"
              />
            </div>
          </li>
        </ul>

        <div
          v-if="total > perPage"
          class="flex flex-col items-center gap-3 border-t border-surface-200 pt-4 sm:flex-row sm:justify-between dark:border-surface-700"
        >
          <p class="text-sm text-surface-500 dark:text-surface-400">
            {{ rangeLabel }}
          </p>
          <Paginator
            :first="(page - 1) * perPage"
            :rows="perPage"
            :total-records="total"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            @page="onPageChange"
          />
        </div>
      </template>
    </AppCard>
  </div>
</template>